import { CompRef } from '@wix/document-services-types';
import type { IPlatformInfraPublicAPI } from '../platformInfraPublicAPI';
import {
  ACTIONS,
  Action,
  DashboardAction as BaseDashboardAction,
  Execution,
  CssProperties,
  CssCustomProperties,
  Data,
} from '@wix/component-protocol/schema';
import { CssPropertyType } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import { Host } from '@wix/sdk-types';
import type {
  AbstractVirtualManifestBuilder,
  IActionableElementsResult,
  IVirtualBuilder,
  NativeActionsList,
  DerivedNativeActions,
  StylesDerivedNativeActionNames,
} from '@wix/editor-platform-manifest-services';
import { StylesDerivedNativeActions } from '@wix/editor-platform-manifest-services';

export { NativeActionsList } from '@wix/editor-platform-manifest-services';

import { ICustomisationAPI } from './customisationAPI';
import { ActionName } from '../manifestServices';

import { EditorPointer } from '@wix/editor-document-logic-api';

type ActionType = 'native' | 'custom' | 'system';

const SystemActionsList = {
  upgrade: 'upgrade',
  comments: 'comments',
  more: 'more',
  hide: 'hide',
  brandStyles: 'brandStyles',
  alignment: 'alignment',
  groupElements: 'groupElements',
  aiTools: 'aiTools',
  background: 'background',
  backgroundOpacity: 'backgroundOpacity',
  interchangeComponent: 'interchangeComponent',
  socialPlayerChangeSource: 'socialPlayerChangeSource',
  editCustomComponent: 'editCustomComponent',
  superGridSwapCells: 'superGridSwapCells',
  superGridMergeCells: 'superGridMergeCells',
  superGridSplitCellHorizontal: 'superGridSplitCellHorizontal',
  superGridSplitCellVertical: 'superGridSplitCellVertical',
  popupContainerSettings: 'popupContainerSettings',
  manageData: 'manageData',
} as const;

type ActionMetadata = {
  tags?: string[];
  origin: string;
};

type NameByType<T extends Record<string, string>> = T[keyof T];

type NativeActionNames = NameByType<typeof NativeActionsList>;
type SystemActionNames = NameByType<typeof SystemActionsList>;

type ActionNames = NativeActionNames | SystemActionNames;

interface Executable<
  TExecute extends (...args: any) => unknown = () => unknown,
> {
  execute: TExecute;
}

type ActionCustomizationFields = {
  tags?: string[];
} & Omit<Action, 'hidden' | 'execution'>;

type ExecutableActionUiCustomization = {
  icon?: React.ReactNode | string;
  tooltip?: string;
  label?: string;
  displayMode?: 'icon' | 'text' | 'icon-and-text';
  isSelected?: () => boolean;
};

type ElementActionUiCustomization = {
  interactiveActionElement?: React.ReactNode;
};

type ActionUiCustomization =
  | ExecutableActionUiCustomization
  | ElementActionUiCustomization;

type ActionStylePreset = {
  id: string;
  thumbnail: React.ComponentType<{ isSelected: boolean }>;
  // style values are any in component-protocol/schema
  values: Partial<Record<CssPropertyType, any>>;
};

type AiMetadata = {
  /**
   * An LLM friendly explanation of what the action does.
   */
  description: string;
  /**
   * Defines how the action is executed:
   * - `'direct'`: The action is performed immediately without any UI interaction.
   * - `'ui'`: Opens a user interface for the action (e.g., panel, dialog, modal).
   */
  actionMode: 'direct' | 'ui';
};

interface EditActionBase<TActionName extends string = string>
  extends Executable,
    ActionCustomizationFields {
  actionName: TActionName;
  displayName: string;
  actionId?: string;
  /**
   * Control if this action can be overridden by an execution defined by a user in the manifest.
   */
  canBeOverriddenBy?: (execution?: Execution) => boolean;
  uiCustomization?: ActionUiCustomization;
  stylePresets?: ActionStylePreset[];
  shouldBeExcluded?: () => boolean;
  /**
   * Metadata describing an edit action to be used by AI.
   */
  aiMetadata?: AiMetadata;
}

interface DashboardAction<TActionName extends string = string>
  extends EditActionBase<TActionName>,
    Required<Pick<BaseDashboardAction, 'dashboardPageId'>> {}

export interface IActionPanelAPI {
  getContent: () => React.ReactNode | undefined;
  getMinimizedContent: () => React.ReactNode | undefined;
  /**
   * @deprecated use `getHostContext` instead
   */
  getHost: (props?: {
    editorPointers?: EditorPointer<CompRef>[];
  }) => Promise<Host | undefined>;
  getHostContext: (props?: {
    editorPointers?: EditorPointer<CompRef>[];
    contextBuilder?: (
      builder: AbstractVirtualManifestBuilder,
    ) => IVirtualBuilder | void;
  }) => Promise<{
    getHost(): Host | undefined;
  }>;
}

export interface IActionContextAPI {
  getData: () => Data[string][];
  getStyle: () => {
    cssProperties?: CssProperties;
    cssCustomProperties?: CssCustomProperties;
  };
  getDisplayName: () => string;
  getActionDefinition: () => Action | DashboardAction;
  getAutoInjectedEditorPointers: () => EditorPointer<CompRef>[];
  getOrigin: () => string | undefined;
  getActionableElements: () => IActionableElementsResult | null;
}

interface ContextBase {
  editorPointers: EditorPointer<CompRef>[];
  /**
   * @deprecated
   * backward compatibility - use `editorPointers` instead
   */
  editorPointer: EditorPointer<CompRef>;
}

interface SystemActionContext extends ContextBase {
  contextAPI: IActionContextAPI;
}

interface NativeActionContext extends ContextBase {
  customisationAPI: ICustomisationAPI;
  actionPanelAPI: IActionPanelAPI;
  contextAPI: IActionContextAPI;
}

type EditActionDefinition<TActionName extends string = string> =
  | EditActionBase<TActionName>
  | DashboardAction<TActionName>;

type HostEditActionDefinition = EditActionDefinition<ActionNames>;

type ActionFactory<T, Definition extends EditActionDefinition> = (
  context: T,
) => Definition;

type SystemEditActionFactory = ActionFactory<
  SystemActionContext,
  EditActionDefinition<SystemActionNames>
>;

type NativeEditActionFactory = ActionFactory<
  NativeActionContext,
  EditActionDefinition<NativeActionNames>
>;

type ExtensibleSystemEditActionFactory = ActionFactory<
  SystemActionContext,
  EditActionDefinition<string>
>;

type EditActionsDictionary<T> = Partial<Record<ActionNames, T>>;

type HostEditAction =
  | SystemEditActionFactory
  | NativeEditActionFactory
  | ExtensibleSystemEditActionFactory;

type HostEditActionByName<TActionName extends string> =
  TActionName extends NativeActionNames
    ? NativeEditActionFactory
    : TActionName extends SystemActionNames
    ? SystemEditActionFactory
    : ExtensibleSystemEditActionFactory;

type HostEditActionsDictionary<TActionName extends string = ActionNames> = {
  [K in TActionName]: HostEditActionByName<K>;
};

const ExecutionMethodsList = {
  data: `${ACTIONS.ACTION_TYPE.data}`,
  style: `${ACTIONS.ACTION_TYPE.style}`,
  codePanel: `${ACTIONS.ACTION_TYPE.panel}-${ACTIONS.PANEL_CONTENT_TYPE.code}`,
  urlPanel: `${ACTIONS.ACTION_TYPE.panel}-${ACTIONS.PANEL_CONTENT_TYPE.url}`,
  unknown: `${ACTIONS.ACTION_TYPE.panel}-${ACTIONS.PANEL_CONTENT_TYPE.UNKNOWN_PanelContentType}`,
} as const;

type ExecutionMethodNames = NameByType<typeof ExecutionMethodsList>;

type PanelSize = NonNullable<NonNullable<Execution['panel']>['inline']>['size'];

type PanelMode = NonNullable<
  NonNullable<Execution['panel']>['inline']
>['panelMode'];

type PlatformFrameProps = {
  appDefinitionId: string;
  getPlatformInfraAPI: () => Promise<IPlatformInfraPublicAPI>;
  url: string;
  displayName?: string | null;
  panelSize?: PanelSize;
  iframeProps?: React.IframeHTMLAttributes<HTMLIFrameElement>;
  panelMode?: PanelMode;
  helpId?: string;
  onUnmount?: () => void;
};

type CodePanelCustomisation = {
  panelContent: React.ReactNode;
  panelSize?: PanelSize;
  cssUrl?: string | null;
  panelMode?: PanelMode;
};

interface IDataCustomisation {
  panelContent: React.ReactNode;
}

interface IStyleCustomisation {
  panelContent: React.ReactNode;
}

interface IGroupCustomisation {
  panelContent: React.ReactNode;
}

type ExecutionMethod<
  TPanelCustomisation extends
    | HTMLElement
    | CodePanelCustomisation
    | IDataCustomisation
    | IStyleCustomisation
    | PlatformFrameProps,
> = (
  panelCustomisation: TPanelCustomisation,
  displayName: string,
  actionId?: string,
) => unknown;

type ExecutionMethodsDictionary = {
  [ExecutionMethodsList.data]: () => ExecutionMethod<IDataCustomisation>;
  [ExecutionMethodsList.style]: () => ExecutionMethod<IStyleCustomisation>;
  [ExecutionMethodsList.codePanel]: ExecutionMethod<CodePanelCustomisation>;
  [ExecutionMethodsList.urlPanel]: ExecutionMethod<PlatformFrameProps>;
  [ExecutionMethodsList.unknown]: () => unknown;
};

export {
  ActionType,
  StylesDerivedNativeActions,
  SystemActionsList,
  DerivedNativeActions,
  ActionMetadata,
  ActionNames,
  NameByType,
  SystemActionNames,
  NativeActionNames,
  EditActionBase,
  DashboardAction,
  ContextBase,
  NativeActionContext,
  SystemActionContext,
  HostEditActionDefinition,
  ActionFactory,
  EditActionsDictionary,
  HostEditActionsDictionary,
  EditActionDefinition,
  HostEditAction,
  ExecutionMethodNames,
  ActionUiCustomization,
  AiMetadata,
  ExecutableActionUiCustomization,
  ElementActionUiCustomization,
  ExecutionMethodsList,
  ExecutionMethodsDictionary,
  ExecutionMethod,
  PlatformFrameProps,
  CodePanelCustomisation,
  IDataCustomisation,
  IStyleCustomisation,
  IGroupCustomisation,
  StylesDerivedNativeActionNames,
  SystemEditActionFactory,
  NativeEditActionFactory,
};
